import {
  Component,
  input,
  output,
  forwardRef,
  computed,
  signal,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type TextInputType =
  | 'text'
  | 'number'
  | 'email'
  | 'password'
  | 'tel'
  | 'url'
  | 'date'
  | 'time'
  | 'datetime-local';

@Component({
  selector: 'sb-text-input',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="w-full flex flex-col gap-y-1">
      @if (label() && !hideLabel()) {
      <label
        [for]="id()"
        class="block text-sm  text-gray-700 transition-colors duration-200"
        [class.sr-only]="hideLabel()"
      >
        {{ displayLabel() }}
        @if (required()) {
        <span class="text-red-500">*</span>
        }
      </label>
      }

      <input
        [id]="id()"
        [name]="name() || id()"
        [step]="step()"
        [min]="min()"
        [max]="max()"
        [type]="type()"
        [autocomplete]="autocomplete()"
        [required]="required()"
        [disabled]="readonly()"
        [placeholder]="placeholder()"
        [value]="value()"
        (input)="onInput($event)"
        (blur)="onBlur()"
        (focus)="onFocus()"
        [class]="inputClasses()"
      />

      @if (errorMessage()) {
      <p
        class="text-xs text-red-600 animate-slideInLeft"
        style="animation-delay: 50ms;"
      >
        {{ errorMessage() }}
      </p>
      } @if (helpText() && !errorMessage()) {
      <p class="text-xs text-gray-400">
        {{ helpText() }}
      </p>
      }
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextInputComponent),
      multi: true,
    },
  ],
})
export class TextInputComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);

  // Input signals
  id = input.required<string>();
  name = input<string>();
  label = input<string>();
  step = input<string>('1');
  min = input<string>('0');
  max = input<string>('100');
  type = input<TextInputType>('text');
  placeholder = input<string>('');
  autocomplete = input<string>();
  required = input<boolean>(false);
  readonly = input<boolean | null | undefined>(null);
  hideLabel = input<boolean>(false);
  errorMessage = input<string | null | undefined>(null);
  helpText = input<string>();
  variant = input<'default' | 'stacked'>('default');
  size = input<'sm' | 'md' | 'lg'>('md');
  autofocus = input<boolean>(false);
  isCurrency = input<boolean>(false);

  // Output signals
  inputChange = output<string>();
  inputBlur = output<void>();
  inputFocus = output<void>();

  value = signal('');

  private onChange = (value: string) => {};
  private onTouched = () => {};

  displayLabel = computed(() => {
    const labelText = this.label() ?? '';

    if (!this.isCurrency()) {
      return labelText;
    }

    const rawValue = this.value();
    if (rawValue === '') {
      return labelText;
    }

    const numericValue = Number.parseFloat(rawValue);
    if (Number.isNaN(numericValue)) {
      return labelText;
    }

    const formattedValue = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);

    return labelText ? `${labelText} (${formattedValue})` : formattedValue;
  });

  inputClasses = computed(() => {
    const baseClasses = [
      'appearance-none relative block w-full px-3 py-2 border rounded-md',
      'placeholder-gray-300 text-gray-900 bg-white',
      'focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10',
      'transition-all duration-200 ease-in-out',
    ];

    // Size classes
    const sizeClasses = {
      sm: 'text-xs py-1.5',
      md: 'text-sm py-2',
      lg: 'text-base py-3',
    };

    // Variant classes
    const variantClasses = {
      default: 'rounded-md',
      stacked: 'rounded-none first:rounded-t-md last:rounded-b-md',
    };

    // State classes
    const stateClasses = this.errorMessage()
      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300';

    const disabledClasses = this.readonly()
      ? 'bg-gray-100 text-gray-400 cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500'
      : '';

    return [
      ...baseClasses,
      sizeClasses[this.size()],
      variantClasses[this.variant()],
      stateClasses,
      disabledClasses,
    ]
      .filter(Boolean)
      .join(' ');
  });

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value.set(target.value);
    this.onChange(target.value);
    this.inputChange.emit(target.value);
  }

  onBlur(): void {
    this.onTouched();
    this.inputBlur.emit();
  }

  onFocus(): void {
    this.inputFocus.emit();
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value.set(value ?? '');
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Note: We can't directly set the disabled signal from here
    // The parent component should handle this through the disabled input
  }
}
